// Pure decision helpers for NudgeBanner. Kept free of React so they can be unit-tested
// without an RTL harness (this repo has none).
//
// The wire shapes (NudgePayload/NudgeCta/NudgeEventProps/NudgeSurface) come from
// the shared bt-common source of truth — do NOT re-declare them here. Re-exported
// so existing importers of these names from this module keep resolving.

import type {
  NudgeCta,
  NudgeId,
  NudgePayload,
  NudgeEventProps,
  NudgeSurface,
  ReviewAskNudgeId,
} from '@bt-common/nudges/nudge.types';
import { isReviewAskNudgeId } from '@bt-common/nudges/nudge.types';

export type { NudgeCta, NudgePayload, NudgeEventProps, NudgeSurface };

/** Server nudge that asks an outdated extension to update; the extension renders it, the web app does not. */
export const EXT_UPDATE_NUDGE_ID = 'ext_update_2026q4';

/** #669: nudges whose whole ask is "connect a Gateway". */
const GATEWAY_CONNECT_NUDGE_IDS: ReadonlySet<NudgeId> = new Set<NudgeId>(['trial_engine_connect_2026q4']);

/**
 * #669 hide-on-connect: a Gateway-connect nudge has done its job once a Gateway is connected.
 * The cached get-nudge payload would otherwise keep showing it until the next refetch, including
 * on /connections right after the scan.
 */
export function isNudgeSatisfied(nudgeId: NudgeId, isGatewayConnected: boolean): boolean {
  return isGatewayConnected && GATEWAY_CONNECT_NUDGE_IDS.has(nudgeId);
}

/**
 * #690: a nudge is pointless on the page its CTA leads to. Hidden there on the web (the extension has
 * no app route). Only the checkout page (upgrade nudges) and /connections (Gateway connect/setup
 * nudges, where the user connects); other CTA targets keep their banner.
 */
const HIDE_ON_TARGET_PATHS: ReadonlySet<string> = new Set(['/account-subscription', '/connections']);
export function isOnCtaTarget(ctaUrl: string, pathname: string | undefined): boolean {
  return !!pathname && HIDE_ON_TARGET_PATHS.has(pathname) && ctaUrl.split(/[?#]/)[0] === pathname;
}

const EXTENSION_APP_ORIGIN = 'https://app.blueticks.co';

/**
 * Dunning precedence + payload gate.
 * - dunning visible => never render the nudge (dunning wins), even with a payload.
 * - no payload (null/undefined) => render nothing.
 * - payload present + no dunning => render.
 */
export function shouldRenderNudge(
  dunningVisible: boolean,
  payload: NudgePayload | null | undefined,
  surface: NudgeSurface = 'extension'
): boolean {
  if (dunningVisible) return false;
  if (!payload) return false;
  // #478: on the web app the update prompt is the presence-driven ExtensionUpdateBanner.
  if (surface === 'web' && String(payload.nudgeId) === EXT_UPDATE_NUDGE_ID) return false;
  return true;
}

/** Event-props shape stamped on every nudge_* event. */
export function buildNudgeEventProps(
  payload: Pick<NudgePayload, 'nudgeId' | 'arm'>,
  surface: NudgeSurface
): NudgeEventProps {
  return {
    nudgeId: payload.nudgeId,
    arm: payload.arm,
    surface,
  };
}

/**
 * Resolve the CTA target per surface.
 * - web: SPA-relative url is used unchanged (react-router navigate).
 * - extension: a relative url is made absolute against app.blueticks.co (opened in a new tab).
 *   An already-absolute url is returned unchanged.
 */
export function resolveCtaUrl(url: string, surface: NudgeSurface): string {
  if (surface === 'web') return url;
  // extension
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${EXTENSION_APP_ORIGIN}${path}`;
}

/** #787: where a review ask is rendered. The banner owns the 50-sends ask, the finish-screen card owns the campaign ask. */
export type ReviewAskPlacement = 'banner' | 'card';

const REVIEW_ASK_PLACEMENT: Record<ReviewAskNudgeId, ReviewAskPlacement> = {
  review_ask_sends_2026q4: 'banner',
  review_ask_campaign_2026q4: 'card',
  review_ask_renewal_2026q4: 'banner',
};

/** Hosts a review ask may send the user to. Anything else (or non-https) never renders the ask. */
export const REVIEW_ASK_ALLOWED_HOSTS: readonly string[] = [
  'chromewebstore.google.com',
  'trustpilot.com',
  'www.trustpilot.com',
];

export function isAllowedReviewUrl(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && REVIEW_ASK_ALLOWED_HOSTS.includes(u.hostname);
  } catch {
    return false;
  }
}

/** Review asks never render while a paywall, an error or the Crisp panel is up (dunning is handled by shouldRenderNudge). */
export function isReviewAskRenderable(
  payload: NudgePayload | null | undefined,
  placement: ReviewAskPlacement,
  ctx: { blockers: readonly string[] }
): boolean {
  if (!payload || !isReviewAskNudgeId(payload.nudgeId)) return false;
  if (ctx.blockers.length > 0) return false;
  if (!isAllowedReviewUrl(payload.cta?.url)) return false;
  return REVIEW_ASK_PLACEMENT[payload.nudgeId] === placement;
}

export function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}
